import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { exerciseSteps } from '../../lib/course-completion';
import type { LessonExercise, SelfMark } from '../../types/course';
import { CoursePictures } from './CoursePictures';
import styles from './Course.module.css';
import { InlineText } from './InlineText';

interface Props {
  exercise: LessonExercise;
  saved: { text: string; selfMark: SelfMark | null } | undefined;
  onSave: (text: string) => Promise<void>;
  onMark: (mark: SelfMark) => Promise<void>;
  /** Reports whether the textarea differs from the saved answer. */
  onDirtyChange?: (id: string, dirty: boolean) => void;
  error?: string | null;
  /** Opens the course conversation after saving a changed answer. */
  onDiscuss?: () => void;
}

/** The three steps shown above «Твой ответ» (keys under lesson.steps). */
const STEP_KEYS = ['write', 'compare', 'mark'] as const;

export function Exercise({ exercise, saved, onSave, onMark, onDirtyChange, error, onDiscuss }: Props): JSX.Element {
  const { t } = useTranslation('course');
  const [text, setText] = useState(saved?.text ?? '');
  const [showHint, setShowHint] = useState(false);
  const [showSolution, setShowSolution] = useState(false);
  const [busy, setBusy] = useState(false);
  const id = exercise.id;
  const savedText = saved?.text ?? '';
  const hasSaved = savedText.trim().length > 0;
  const unchanged = text === savedText;
  const dirty = !unchanged;
  const selfMark = saved?.selfMark ?? null;
  const steps = exerciseSteps({ savedText, text, solutionOpen: showSolution, selfMark });

  useEffect(() => {
    onDirtyChange?.(id, dirty);
    return () => onDirtyChange?.(id, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dirty]);

  async function run(fn: () => Promise<void>): Promise<void> {
    setBusy(true);
    try { await fn(); } catch { /* the page shows the error */ } finally { setBusy(false); }
  }

  return (
    <article className={styles.card} data-testid={`exercise-${id}`}>
      <h4 className={styles.h4}><InlineText text={exercise.title} /></h4>
      <p className={styles.text}><InlineText text={exercise.prompt} /></p>
      <CoursePictures images={[exercise.image, ...(exercise.extraImages ?? [])]} />
      {exercise.hint && (
        <>
          <button type="button" className={styles.linkBtn} onClick={() => setShowHint((v) => !v)} data-testid={`hint-toggle-${id}`}>
            {t('lesson.hint')}
          </button>
          {showHint && <p className={styles.text} data-testid={`hint-${id}`}><InlineText text={exercise.hint} /></p>}
        </>
      )}
      <ol className={styles.steps} aria-label={t('lesson.steps.label')} data-testid={`steps-${id}`}>
        {STEP_KEYS.map((key, i) => {
          const state = steps[i];
          return (
            <li
              key={key}
              className={`${styles.step} ${styles[`step_${state}`] ?? ''}`}
              data-state={state}
              aria-current={state === 'current' ? 'step' : undefined}
              data-testid={`step-${id}-${i + 1}`}
            >
              <span className={styles.stepNum} aria-hidden="true">{state === 'done' ? '✓' : i + 1}</span>
              <span>
                {t(`lesson.steps.${key}`)}
                {state === 'done' && <span className={styles.srOnly}> — {t('lesson.steps.done')}</span>}
              </span>
            </li>
          );
        })}
      </ol>
      <label className={styles.label} htmlFor={`ans-${id}`}>{t('lesson.yourAnswer')}</label>
      <textarea
        id={`ans-${id}`}
        className={styles.textarea}
        rows={4}
        maxLength={5000}
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid={`answer-${id}`}
      />
      <button
        type="button"
        className={styles.secondary}
        disabled={busy || text.trim().length === 0 || unchanged}
        onClick={() => void run(() => onSave(text))}
        data-testid={`save-${id}`}
      >
        {t('lesson.saveAnswer')}
      </button>
      {onDiscuss && (
        <div className={styles.reviewAsk}>
          <button
            type="button"
            className={styles.secondary}
            disabled={busy || (dirty && !text.trim())}
            onClick={() => void run(async () => {
              if (dirty) await onSave(text);
              onDiscuss();
            })}
            data-testid={`discuss-${id}`}
          >
            {t('chat.discuss')}
          </button>
          {dirty && <p className={styles.caption}>{t('chat.saveAndDiscuss')}</p>}
        </div>
      )}
      {hasSaved && !showSolution && (
        <button
          type="button"
          // the card's main action while «Открой решение» is the current step; outlined once marked or while editing
          className={steps[1] === 'current' ? styles.solid : styles.secondary}
          onClick={() => setShowSolution(true)}
          data-testid={`show-solution-${id}`}
        >
          {t('lesson.showSolution')}
        </button>
      )}
      {hasSaved && showSolution && (
        <div className={styles.solution}>
          <p className={styles.text} data-testid={`solution-${id}`}><InlineText text={exercise.solution} /></p>
          {!dirty && <p className={styles.markPrompt} data-testid={`mark-prompt-${id}`}>{t('lesson.markPrompt')}</p>}
          {!dirty && (
            <div className={styles.row}>
              <button
                type="button"
                className={`${styles.secondary} ${selfMark === 'ok' ? styles.active : ''}`}
                disabled={busy}
                aria-pressed={saved?.selfMark === 'ok'}
                onClick={() => void run(() => onMark('ok'))}
                data-testid={`mark-ok-${id}`}
              >
                {t('lesson.markOk')}
              </button>
              <button
                type="button"
                className={`${styles.secondary} ${saved?.selfMark === 'wrong' ? styles.active : ''}`}
                disabled={busy}
                aria-pressed={saved?.selfMark === 'wrong'}
                onClick={() => void run(() => onMark('wrong'))}
                data-testid={`mark-wrong-${id}`}
              >
                {t('lesson.markWrong')}
              </button>
            </div>
          )}
        </div>
      )}
      {!dirty && saved?.selfMark && (
        <p className={styles.status} data-testid={`mark-state-${id}`}>
          {saved.selfMark === 'ok' ? t('lesson.markedOk') : t('lesson.markedWrong')}
        </p>
      )}
      {error && <p role="alert" className={styles.error} data-testid={`error-${id}`}>{error}</p>}
    </article>
  );
}
